import { useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { CardAction } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { hasPluginPermission, usePermission } from "imports/Utils";
import { useConfirm } from "components/hooks/useConfirm";
import {
	CloseDeclarationDocument,
	OpenDeclarationDocument,
	ReadyFinanciallyDocument,
	UnreadyFinanciallyDocument,
} from "__generated__/view/declarations/graphql/declaration.mutations.generated";
import { useStoreState } from "ezStore";
import CalculateIcon from "@mui/icons-material/Calculate";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/LockOpen";
import UnlockIcon from "@mui/icons-material/NoEncryption";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import DoNotDisturbOffIcon from "@mui/icons-material/DoNotDisturbOffOutlined";
import InvoiceLineEditDialog from "view/invoicing/components/dialogs/InvoiceLineEditDialog";
import DeclarationDialog from "view/declarations/components/dialogs/DeclarationDialog";
import StatusChanger from "view/statuses/components/changer/StatusChanger";
import { Box } from "@mui/material";
import { HistoryEntity, StatusType } from "__generated__/enums";
import { StatusDeclarationEntity } from "view/statuses/types/status";
import EntityHistoryDialog from "components/entityHistory/components/dialogs/EntityHistoryDialog";
import { EntityHistoryType } from "components/entityHistory/utils/entityHistory.utils";
import HistoryIcon from "@mui/icons-material/History";

type UseDeclarationActionsProps = {
	declaration: StatusDeclarationEntity & {
		closedOn?: number | null;
		financiallyReadiedOn?: number | null;
		invoiceLines?: ReadonlyArray<{ invoiceReference?: string | null } | null> | null;
	} | null;
	charter?: {
		id: number;
		type?: string;
		creditor?: { id: number; name: string } | null;
	};
	invoiceGroupType?: { id?: number | null; name?: string | null } | null;
	isEditable?: boolean;
	simple?: boolean;
	refetch?: () => void;
};

export const useDeclarationActions = ({
	declaration,
	charter,
	isEditable = true,
	refetch,
	invoiceGroupType,
	simple = false,
}: UseDeclarationActionsProps) => {
	const { isReadOnly } = usePermission();
	const session = useStoreState(store => store.session);
	const disableInvoiceLineEditing = hasPluginPermission(session, "disableInvoiceLineEditing");
	const tableRef = useRef<{ clearSelection: () => void }>(null);
	const confirm = useConfirm();

	const closed = !!declaration?.closedOn || isReadOnly;
	const financiallyReady = !!declaration?.financiallyReadiedOn;

	const invoiceLines = declaration?.invoiceLines ?? [];
	const allLinesApproved = invoiceLines.length > 0
		&& invoiceLines.every(line => !!line?.invoiceReference?.trim());

	const [ readyFinancially, { loading: readyLoading } ] = useMutation(ReadyFinanciallyDocument, {
		onCompleted: () => refetch?.(),
	});
	const [ unreadyFinancially, { loading: unreadyLoading } ] = useMutation(UnreadyFinanciallyDocument, {
		onCompleted: () => refetch?.(),
	});
	const [ closeDeclaration, { loading: closeLoading } ] = useMutation(CloseDeclarationDocument, {
		onCompleted: () => refetch?.(),
	});
	const [ openDeclaration, { loading: openLoading } ] = useMutation(OpenDeclarationDocument, {
		onCompleted: () => refetch?.(),
	});

	const [ declareDialogOpen, setDeclareDialogOpen ] = useState(false);
	const [ historyDialogOpen, setHistoryDialogOpen ] = useState(false);
	const [ addLineDialogOpen, setAddLineDialogOpen ] = useState(false);

	const statusActions: CardAction[] = declaration?.lastStatusChange ? [ {
		render: () => (
			<Box sx={{ margin: 1 }}>
				<StatusChanger
					statusType={StatusType.Declaration}
					entity={declaration}
					showHistory
					refetch={() => refetch?.()}
				/>
			</Box>
		),
	} ] : [];

	const cardActions: CardAction[] = simple ? statusActions : [
		...statusActions,
		...(!financiallyReady && !closed && !isReadOnly && charter ? [ {
			icon: <CalculateIcon fontSize="small" />,
			tooltip: intl.formatMessage({ id: "DECLARE" }),
			disabled: !isEditable,
			onClick: () => setDeclareDialogOpen(true),
		} ] : []),
		...(!financiallyReady && !closed && !disableInvoiceLineEditing && charter?.creditor && declaration ? [ {
			icon: <AddIcon fontSize="small" />,
			tooltip: intl.formatMessage({ id: "ADD_BUTTON" }),
			disabled: !isEditable,
			onClick: () => setAddLineDialogOpen(true),
		} ] : []),
		...(allLinesApproved && !financiallyReady && !disableInvoiceLineEditing && !closed && !isReadOnly && charter && declaration ? [ {
			icon: <LockIcon fontSize="small" />,
			tooltip: allLinesApproved
				? intl.formatMessage({ id: "READY_FINANCIALLY" })
				: intl.formatMessage({ id: "NOT_ALL_INVOICELINES_APPROVED" }),
			disabled: !isEditable || !allLinesApproved || readyLoading,
			onClick: () => {
				confirm({
					type: "WARNING",
					title: intl.formatMessage({ id: "DECLARATION_FINANCIAL_READY_CONFIRM" }),
				})
					.then(() => readyFinancially({ variables: { id: declaration.id } }))
					.catch(() => { /* cancelled */ });
			},
		} ] : []),
		...(financiallyReady && !disableInvoiceLineEditing && !isReadOnly && charter && declaration ? [ {
			icon: <UnlockIcon fontSize="small" />,
			tooltip: intl.formatMessage({ id: "UNREADY_FINANCIALLY" }),
			disabled: !isEditable || unreadyLoading,
			onClick: () => unreadyFinancially({ variables: { id: declaration.id } }),
		} ] : []),
		...(allLinesApproved && !disableInvoiceLineEditing && !closed && !isReadOnly && charter && declaration ? [ {
			icon: <DoNotDisturbIcon fontSize="small" />,
			tooltip: allLinesApproved
				? intl.formatMessage({ id: "CLOSE" })
				: intl.formatMessage({ id: "NOT_ALL_INVOICELINES_APPROVED" }),
			disabled: !isEditable || !allLinesApproved || closeLoading,
			onClick: () => {
				confirm({
					type: "WARNING",
					title: intl.formatMessage({ id: "DECLARATION_CLOSE_CONFIRM" }),
				})
					.then(() => closeDeclaration({ variables: { id: declaration.id } }))
					.catch(() => { /* cancelled */ });
			},
		} ] : []),
		...(declaration && !!declaration.closedOn && !disableInvoiceLineEditing && !isReadOnly && charter ? [ {
			icon: <DoNotDisturbOffIcon fontSize="small" />,
			tooltip: intl.formatMessage({ id: "OPEN" }),
			disabled: !isEditable || openLoading,
			onClick: () => openDeclaration({ variables: { id: declaration.id } }),
		} ] : []),
		...( declaration ? [ {
			icon: <HistoryIcon fontSize="small" />,
			tooltip: intl.formatMessage({ id: "ENTITY_HISTORY" }),
			onClick: () => setHistoryDialogOpen(true),
		} ] : []),
	];

	// Dialogs
	const dialogs = <>
		{declareDialogOpen && charter && (
			<DeclarationDialog
				open={declareDialogOpen}
				onClose={() => setDeclareDialogOpen(false)}
				charterId={charter.id}
				declarationId={declaration?.id}
				invoiceGroupType={invoiceGroupType}
				onSaved={() => refetch?.()}
			/>
		)}

		{historyDialogOpen && declaration && (
			<EntityHistoryDialog
				open={historyDialogOpen}
				onClose={() => setHistoryDialogOpen(false)}
				entity={HistoryEntity.Declaration}
				entityId={declaration.id}
				title={intl.formatMessage({ id: "ENTITY_HISTORY" })}
				maxWidth="md"
				type={EntityHistoryType.Absolute}
			/>
		)}

		{addLineDialogOpen && declaration && charter?.creditor && (
			<InvoiceLineEditDialog
				open={addLineDialogOpen}
				onClose={() => setAddLineDialogOpen(false)}
				invoiceLine={null}
				refetch={() => refetch?.()}
				charter={{ id: charter.id, creditor: charter.creditor }}
				declarationId={declaration.id}
			/>
		)}
	</>;

	return {
		cardActions,
		tableRef,
		dialogs,
		disabled: closed,
	};
};
